import React, { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { ConsumoCombustible } from "../../types/consumoCombustible";
import { useAuth } from "../../context/AuthContext";
import { Vehiculo } from '../../types/vehiculo';
import { getVehiculoOracleByPlaca, getVehiculoByPlaca, upsertVehiculo, getUltimosKilometrosYHoras } from '../../services/Combustible/vehiculoService';
import { fetchPrecioCombustible, fetchPromedioMediaConsumo } from '../../services/Combustible/consumoService';
import { insertConsumo } from '../../services/Combustible/consumoService';
import { fetchEmployeeByCode, upsertEmployee } from '../../services/General/employeeService';
import { Employee } from '../../types/employee';
import HistorialConsumoCombustible from './HistorialConsumoCombustible';
import { getLocalDateString } from '../../utils/fechaUtil';
import { PrecioCombustible } from '../../types/precioCombustible';
import { formatNumber } from '../../utils/numberUtil';
import { PromedioMediaConsumo } from '../../types/promedioMediaConsumo';

const factorLitros = 8;

const ConsumoCombustibleVehiculo: React.FC = () => {
    const focusRef = useRef<HTMLInputElement>(null);
    const { user } = useAuth();
    const [placa, setPlaca] = useState<string>('');
    const [vehicleData, setVehicleData] = useState<Vehiculo | null>(null);
    const [employeeData, setEmployeeData] = useState<Employee | null>(null);
    const [consumptionData, setConsumptionData] = useState<ConsumoCombustible>({
        consumoId: 0,
        chofer: '',
        placa: '',
        kilometros: 0,
        precioTotal: 0,
        cantidadLitros: 0,
        cantidadGalones: 0,
        horasUnidadFrio: 0,
        fechaRegistro: getLocalDateString(),
        created_by: '',
        date_created: new Date().toISOString().split("T")[0] // Fecha actual en formato YYYY-MM-DD,
    });
    const [error, setError] = useState<string | null>(null);
    const [errorEmployee, setErrorEmployee] = useState<string | null>(null);
    const [isRegisterDisabled, setIsRegisterDisabled] = useState<boolean>(true);
    const [isFromOracle, setIsFromOracle] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>("captura");
    const [kilometrajeAnterior, setKilometrajeAnterior] = useState<number>(0);
    const [horasUnidadFrioAnterior, setUltimaHorasUnidadFrio] = useState<number>(0);
    const [showToast, setShowToast] = useState(false);
    const [precioCombustible, setPrecioCombustible] = useState<PrecioCombustible>({ PRECIO: 0 });
    const [promedioMediaConsumo, setPromedioMediaConsumo] = useState<PromedioMediaConsumo>({ PromedioGalones: 0, PromedioLitros: 0, MediaLitros: 0, MediaGalones: 0 });
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const limiteAumentoKm = 3000;
    useEffect(() => {
        loadPrecioCombustibleReferencia();
        loadPromedioMediaConsumo();
    }, [])

    useEffect(() => {
        console.log("Datos de consumo actualizados", consumptionData);
        const { chofer, placa, kilometros, cantidadLitros, cantidadGalones, horasUnidadFrio } = consumptionData;
        const isFormValid =
            chofer.trim() !== "" &&
            placa.trim() !== "" &&
            kilometros > 0 &&
            cantidadLitros > 0 &&
            cantidadGalones > 0 &&
            employeeData?.ESTADO === 'A' &&
            (isFromOracle ?
                kilometros > 0 :
                (kilometros > kilometrajeAnterior && (kilometros - kilometrajeAnterior) < limiteAumentoKm)
            ) &&
            /* se desactiva la siguiente linea el 20-06-2024 por requerimiento de usuario final 
               para evitar bloqueos al registrar consumo
            */
            /*(kilometros > kilometrajeAnterior) && ((kilometros - kilometrajeAnterior) < limiteAumentoKm) &&*/
            (horasUnidadFrio === 0 || horasUnidadFrio > horasUnidadFrioAnterior || horasUnidadFrioAnterior === 0);

        setIsRegisterDisabled(!isFormValid);
    }, [consumptionData, employeeData, kilometrajeAnterior, horasUnidadFrioAnterior, isFromOracle]);

    const handlePlaca = (placa: string) => {
        const placaLimpia = placa.replace(/"/g, '');
        setPlaca(placaLimpia);
        console.log("Placa sin comillas", placaLimpia);
    }

    const loadPrecioCombustibleReferencia = async () => {
        const precioReferencia = await fetchPrecioCombustible();
        setPrecioCombustible({ PRECIO: precioReferencia.PRECIO });
    };

    const loadPromedioMediaConsumo = async () => {
        const promedioMediaConsumo = await fetchPromedioMediaConsumo();
        console.log('Promedio/Media: ', promedioMediaConsumo);
        setPromedioMediaConsumo(promedioMediaConsumo);
    };


    const fetchVehiculo = async () => {
        try {
            setError(null);
            setIsFromOracle(false);
            resetForm();
            let data;
            data = await getVehiculoByPlaca(placa);
            console.log('Vehiculo en BD SQL: ', data);
            console.log('Esta en oracle: ', isFromOracle);
            //Si no existe en SQL server se busca en el ERP
            if (!data[0]) {
                //console.log('Entre por donde no existe en SQL Server: ', data.length)
                data = await getVehiculoOracleByPlaca(placa);
                setIsFromOracle(true);
                setKilometrajeAnterior(0);
            }

            if (data.length > 0) {
                setVehicleData(data[0]);
                setIsRegisterDisabled(false);
                setConsumptionData((prevData) => ({
                    ...prevData,
                    placa,
                }));
                const datosHistoriaKmsHrs = await getUltimosKilometrosYHoras(placa);
                console.log('Kilometros anteriores', datosHistoriaKmsHrs.Kilometros);
                const kilometrosNumber = datosHistoriaKmsHrs.Kilometros || 0;
                const horasUnidadFrioAnterior = datosHistoriaKmsHrs.HorasUnidadFrio || 0;
                setKilometrajeAnterior(Number(kilometrosNumber));
                setUltimaHorasUnidadFrio(Number(horasUnidadFrioAnterior));

                console.log('Vehiculo cargado: ', vehicleData);
            } else {
                resetForm();
                setError("No se encontraron vehículos con la placa proporcionada.");
                setIsRegisterDisabled(true);
                setVehicleData(null);
                setIsFromOracle(false);
                Swal.fire({
                    title: 'Error',
                    text: 'No se encontró el vehículo según placa digitada',
                    icon: 'error',
                    confirmButtonText: 'OK'
                });
            }
        } catch (error) {
            console.error("Error al buscar el vehículo:", error);
            setError("No se pudo encontrar el vehículo con la placa proporcionada.");
            setIsRegisterDisabled(true);
            setVehicleData(null);
        }
    };

    const fetchEmpleado = async (code: string) => {
        console.log('Buscando empleado(chofer):', code);
        try {
            let employeeFetchedData = await fetchEmployeeByCode(code);
            console.log('Datos del empleado desde el API: ', employeeFetchedData[0]);
            if (employeeFetchedData) {
                if (employeeFetchedData[0].ESTADO === 'I') {
                    setErrorEmployee('Empleado inactivo');
                    //setEmployeeData(null);
                    setEmployeeData({ NO_EMPLE: '', NOMBRE: '' } as Employee);
                    setIsRegisterDisabled(true);
                    setConsumptionData((prevData) => {
                        const updatedData = {
                            ...prevData,
                            chofer: '',
                        };
                        return updatedData;
                    });
                    Swal.fire({
                        title: "Atención!!",
                        text: "El Empleado: " + code + " está Inactivo",
                        icon: "error",
                        confirmButtonText: 'CANCEL'
                    });
                } else if (employeeFetchedData[0].ESTADO === 'A') {
                    setErrorEmployee(null);
                    setEmployeeData(employeeFetchedData[0]);
                    setIsRegisterDisabled(false);
                    setConsumptionData((prevData) => ({
                        ...prevData,
                        chofer: employeeFetchedData[0].NO_EMPLE,
                    }));
                }

            } else {
                Swal.fire({
                    title: "Atención!!",
                    text: "No se encontró el empleado",
                    icon: "error",
                    confirmButtonText: 'OK'
                });
                setErrorEmployee("No se encontró el empleado digitado");
                setEmployeeData({ NOMBRE: '' } as Employee)
                console.log('Employeee data quedo así: ', employeeData);
                setIsRegisterDisabled(true);
                setVehicleData(null);
            }
        } catch (error) {
            console.error("Error al buscar el chofer:", error);
            setErrorEmployee("No se pudo encontrar el chofer con el código de empleado proporcionado.");
            setEmployeeData({ NOMBRE: '' } as Employee)
            setIsRegisterDisabled(true);
            Swal.fire({
                title: "Atención!!",
                text: "No se encontró el empleado",
                icon: "error",
                confirmButtonText: 'OK'
            });
        }

    };

    const handleEmployeeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        let employeeEntered = value;

        if (name === "chofer" && value.length === 6 && value.startsWith("01")) {
            employeeEntered = value.substring(2);
            console.log('Valor cargado por lector de QR: ', employeeEntered);
        }
        setConsumptionData((prevData) => {
            const updatedData = {
                ...prevData,
                chofer: employeeEntered || '',
            };
            if (name === "chofer" && employeeEntered.length === 4) {
                console.log('digito el chofer: ', value);
                fetchEmpleado(employeeEntered);
            } else {
                setEmployeeData({ NO_EMPLE: '', NOMBRE: '' } as Employee);
                setIsRegisterDisabled(true)
            }
            return updatedData;
        })
    };

    const handleConsumptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        console.log('name', [name], value);
        setConsumptionData((prevData) => {
            const updatedData = {
                ...prevData,
                [name]: name === "horasUnidadFrio" || name === "kilometros" || name === "cantidadLitros" || name === "cantidadGalones"
                    ? parseFloat(value) || 0 // Convertir a número o usar 0 si está vacío
                    : value,
            };
            // Si el campo editado es "litros", calcular galones
            if (name === "cantidadLitros") {
                const cantidadGalones = parseFloat(value) / 3.785; // Convertir a galones
                updatedData.cantidadGalones = parseFloat(cantidadGalones.toFixed(2)); // Redondear a 2 decimales
                updatedData.precioTotal = parseFloat(value) * precioCombustible.PRECIO;
            }
            return updatedData;
        });
    };

    const confirmSaveEvenIfHigh = async () => {
        if (isSubmitting) return;

        const maxEsperado = formatNumber((Number(promedioMediaConsumo.MediaLitros) * factorLitros), 2);
        const result = await Swal.fire({
            title: "Advertencia",
            text: `El valor ingresado supera lo habitual. Se espera un máximo de ${maxEsperado} litros. ¿Desea continuar?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: '<i class="fa fa-check"></i> Continuar',
            cancelButtonText: '<i class="fa fa-times"></i> Corregir',
            confirmButtonColor: "#dc3545", // rojo
            cancelButtonColor: "#6c757d"   // gris
        });

        if (result.isConfirmed) {
            // Si el usuario confirma, se procede a grabar los datos.
            registerConsumption();
        }
        // Si el usuario elige corregir, se cancela la acción y el usuario vuelve a la pantalla.
    };


    const registerConsumption = async () => {
        //Evitar múltiples envíos
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        try {
            if (!vehicleData) {
                setIsSubmitting(false);
                return new Error("vehicleData no está definido");
            }
            const employeeToUpsert = {
                NO_EMPLE: employeeData?.NO_EMPLE || "",
                NOMBRE: employeeData?.NOMBRE || "",
                ESTADO: employeeData?.ESTADO || ""
            };
            try {
                console.log('Datos del empleado a crear: ', employeeToUpsert);
                await upsertEmployee(employeeToUpsert);
            } catch (error: any) {
                throw new Error(`Error al crear el Chofer: ${error.message || "Error desconocido al grabar Chofer"}`);
            }

            //Si el vehículo viene de Oracle es porque no existe en la BD SQL 
            //entonces se registra en SQL
            if (isFromOracle) {
                const updatedVehicleData = {
                    ...vehicleData,
                    created_by: user?.User.email || "",
                };
                try {
                    console.log('Datos del vehículo: ', updatedVehicleData);
                    await upsertVehiculo(updatedVehicleData);
                } catch (error: any) {
                    throw new Error(`Error al crear el vehículo: ${error.message || "Error desconocido al grabar vehiculo"}`);
                }
            }
            //Guardar datos de consumo
            //Definir datos actualizados para el consumo
            const updatedConsumptionData = {
                ...consumptionData,
                created_by: user?.User.email || "",
            };
            //console.log('Datos a enviar al backend', updatedConsumptionData);
            const response = await insertConsumo(updatedConsumptionData);
            if (response.status === 201) {
                setShowToast(true);
                resetForm();
                setPlaca('');
                setTimeout(() => {
                    setShowToast(false);
                    setIsSubmitting(false);
                }, 3000);
            }
            if (focusRef.current) {
                focusRef.current.focus();
            }
        } catch (error) {
            console.error("Error al registrar el consumo:", error);
            Swal.fire({
                title: 'Error',
                text: 'Hubo un error al guardar los datos. ' + error,
                icon: 'error',
                confirmButtonText: 'OK'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setConsumptionData({
            consumoId: 0,
            chofer: '',
            placa: '',
            kilometros: 0,
            precioTotal: 0,
            cantidadLitros: 0,
            cantidadGalones: 0,
            horasUnidadFrio: 0,
            fechaRegistro: getLocalDateString(),
            created_by: '',
            date_created: new Date().toISOString().split("T")[0],
        });
        setVehicleData(null);
        setEmployeeData(null);
        setIsFromOracle(false);
        setKilometrajeAnterior(0);
        setUltimaHorasUnidadFrio(0);
    };

    return (

        <div className='container mt-4'>

            <ul className="nav nav-tabs">
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === "capture" ? "active" : ""}`}
                        onClick={() => setActiveTab("captura")}>
                        Carga Combustible
                    </button>
                </li>
                <li className="nav-item">
                    <button className={`nav-link ${activeTab === "historial" ? "active" : ""}`}
                        onClick={() => setActiveTab("historial")}>
                        Historial
                    </button>
                </li>

            </ul>
            <div className="tab-content">
                {activeTab === "captura" && (
                    <div className="tab-pane active">
                        <div className='row'>
                            {/*Columna Izquierda*/}
                            <div className='col-md-6 border-md'>
                                <h5 className='mb-3 mt-3'>Buscar Vehículo</h5>

                                <div className="mb-3">
                                    <label htmlFor="placa" className="form-label">
                                        Número de Placa:
                                    </label>  {isFromOracle &&
                                        <span className='badge bg-success'>new</span>}
                                    <input
                                        type='text'
                                        id='placa'
                                        className='form-control'
                                        placeholder='Digite una placa...'
                                        value={placa}
                                        onChange={(e) => handlePlaca(e.target.value)}
                                        ref={focusRef}
                                    />
                                </div>
                                <div className="row">
                                    <div className='col'>
                                        <button className="btn btn-primary w-100 mb-3" onClick={fetchVehiculo}>
                                            Buscar
                                            <i className="fa-solid fa-magnifying-glass ms-2"></i>
                                        </button>
                                    </div>
                                    <div className="col">
                                        <button className="btn btn-warning w-100 mb-3" aria-label='Limpiar formulario' onClick={resetForm}>
                                            Reiniciar
                                            <i className="fa fa-eraser me-2"></i>
                                        </button>
                                    </div>
                                </div>


                                {/* Muestra errores */}
                                {error && <div className="alert alert-danger">{error}</div>}


                                {/* Datos del vehículo */}
                                {vehicleData && (
                                    <div className='row'>
                                        <div className="card">
                                            <div className="card-body">
                                                <h5 className="card-title">Datos del Vehículo: {vehicleData.Placa}</h5>
                                                {/* <p><strong>Placa:</strong> {vehicleData.Placa}</p> */}
                                                <p><strong>Centro de costos:</strong> {vehicleData.CentroCosto}-{vehicleData.CentroCostoDesc}</p>
                                                <p><strong>Unidad:</strong> {vehicleData.NumeroUnidad}</p>
                                                <p><strong>Marca/Modelo:</strong> {vehicleData.MarcaDesc} / {vehicleData.ModeloDesc}</p>
                                                <p><strong>Año:</strong> {vehicleData.Anno}</p>
                                                {isFromOracle && (
                                                    <div className="alert alert-info mt-2">
                                                        <i className="fas fa-car me-2"></i>
                                                        <strong>Vehículo nuevo detectado:</strong> El kilometraje se registrará como inicial en el sistema.
                                                    </div>
                                                )}
                                            </div>
                                            <div className="row mb-3 card-footer">
                                                <div className="col-md-6">
                                                    <label htmlFor='kilometrajeAnterior' className='form-label mr-3'>Kilometraje Anterior:</label>
                                                    <div className='form-control bg-light text-end'>{formatNumber(kilometrajeAnterior, 0)}</div>
                                                    {isFromOracle && kilometrajeAnterior === 0 && (
                                                        <small className="form-text text-info">
                                                            <i className="fas fa-info-circle me-1"></i>
                                                            Vehículo nuevo - Sin kilometraje previo
                                                        </small>
                                                    )}
                                                </div>
                                                <div className="col-md-6">
                                                    <label htmlFor='horasUnidadFrioAnterior' className='form-label mr-3'>Horas Unidad Frio Anterior:</label>
                                                    <div className='form-control bg-light text-end'>{formatNumber(horasUnidadFrioAnterior, 0)}</div>
                                                </div>
                                            </div>

                                        </div>

                                    </div>

                                )}
                            </div>
                            {/* Columna derecha */}
                            <div className="col-md-6">
                                <h5 className="mb-3 mt-3">Registrar Consumo de Combustible</h5>
                                <div className="mb-3" hidden>
                                    <label htmlFor="placa" className="form-label">Placa:</label>
                                    <input
                                        type="string"
                                        id="placa"
                                        className="form-control"
                                        name="placa"
                                        value={consumptionData.placa}
                                        readOnly
                                    />
                                </div>
                                <div className="mb-3">
                                    <label htmlFor="chofer" className="form-label">Cód. Empleado (Chofer):</label>
                                    <div className='input-group'>
                                        <input
                                            type="string"
                                            id="chofer"
                                            className="form-control text-end"
                                            name="chofer"
                                            placeholder='Digite el código de empleado del chofer...'
                                            onFocus={(e) => e.target.select()}
                                            value={consumptionData.chofer}
                                            onChange={handleEmployeeChange}
                                        />

                                        <button className='btn btn-online-primary' onClick={() => fetchEmpleado(consumptionData.chofer)} type='button'>
                                            <i className='fas fa-search'></i>
                                        </button>
                                    </div>
                                    <div >
                                        {employeeData?.NOMBRE && <span className='text-success ms-2'>{employeeData?.NOMBRE}</span>}
                                    </div>
                                </div>

                                <div className="mb-3 form-group">
                                    <label htmlFor="kilometros" className="form-label">Kilometraje Actual</label>
                                    <input
                                        type="number"
                                        id="kilometros"
                                        className={`form-control text-end ${kilometrajeAnterior > consumptionData.kilometros ||
                                            (kilometrajeAnterior > 0 && ((consumptionData.kilometros - kilometrajeAnterior) >= limiteAumentoKm))
                                            ? 'is-invalid'
                                            : ''
                                            }`}
                                        name="kilometros"
                                        onFocus={(e) => e.target.select()}
                                        value={consumptionData.kilometros}
                                        onChange={handleConsumptionChange}
                                    />
                                    {!isFromOracle &&kilometrajeAnterior > consumptionData.kilometros && (
                                        <div className='invalid-feedback'>Kilometraje actual debe ser mayor al anterior</div>
                                    )}
                                    {!isFromOracle && kilometrajeAnterior > 0 && ((consumptionData.kilometros - kilometrajeAnterior) >= limiteAumentoKm) && (
                                        <div className='invalid-feedback'>{`El kilometraje actual es demasiado alto en comparación con el anterior. El máximo esperado es de ${kilometrajeAnterior + (limiteAumentoKm - 1)} km. Por favor, verifique el valor.`}</div>
                                    )}
                                    {/* Mensaje especial para vehículos nuevos */}
                                    {isFromOracle && (
                                        <div className="form-text text-info">
                                            <i className="fas fa-info-circle me-1"></i>
                                            Vehículo nuevo: Registre el kilometraje real del vehículo
                                        </div>
                                    )}
                                </div>

                                <div className="mb-3 form-group">
                                    <label htmlFor="precioTotal" className="form-label">Precio Total:</label>
                                    <strong>
                                        <input
                                            type="text"
                                            id='precioTotal'
                                            className="form-control text-end"
                                            name='precioTotal'
                                            onFocus={(e) => e.target.select()}
                                            value={formatNumber(consumptionData.precioTotal, 2)}
                                            onChange={handleConsumptionChange}
                                            readOnly tabIndex={-1}
                                        />
                                    </strong>

                                    <small className="text-muted">{`Precio referencia Combustible: ${formatNumber(precioCombustible.PRECIO, 2)}`}</small>
                                </div>
                                <div className="mb-3 form-group">
                                    <label htmlFor="litros" className="form-label">Cantidad en Litros</label>
                                    <input
                                        type="number"
                                        id="litros"
                                        className={`form-control text-end ${consumptionData.cantidadLitros >= (Number(promedioMediaConsumo.MediaLitros) * factorLitros) ? 'is-invalid' : ''}`}
                                        name="cantidadLitros"
                                        step="0.01"
                                        min="0.01"
                                        max="1000"
                                        placeholder='Ej: 40.42'
                                        onClick={(e) => e.currentTarget.select()}
                                        onFocus={(e) => e.target.select()}
                                        value={consumptionData.cantidadLitros}
                                        onChange={handleConsumptionChange}
                                    />
                                    <small className='text-muted'>
                                        {`Litros promedio: ${promedioMediaConsumo?.MediaLitros}`}
                                    </small>
                                    {consumptionData.cantidadLitros >= (Number(promedioMediaConsumo?.MediaLitros) * factorLitros) && (
                                        <div className="invalid-feedback ">
                                            {`El número de litros ingresado es mucho mayor de lo normal. El máximo esperado es de ${formatNumber((Number(promedioMediaConsumo?.MediaLitros) * factorLitros), 2)} litros. Por favor, verifique el valor.`}
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3 form-group">
                                    <label htmlFor="galones" className="form-label">Cantidad en Galones</label>
                                    <input
                                        type="number"
                                        id="galones"
                                        className="form-control text-end"
                                        name="cantidadGalones"
                                        value={consumptionData.cantidadGalones}
                                        readOnly tabIndex={-1}
                                    />
                                </div>
                                <div className="mb-3 form-group">
                                    <label htmlFor="horasUnidadFrio" className="form-label">Horas Unidad Frio</label>
                                    <input
                                        type="number"
                                        id='horasUnidadFrio'
                                        className={`form-control text-end ${consumptionData.horasUnidadFrio > 0 && consumptionData.horasUnidadFrio <= horasUnidadFrioAnterior ? 'is-invalid' : ''}`}
                                        name='horasUnidadFrio'
                                        onFocus={(e) => e.target.select()}
                                        value={consumptionData.horasUnidadFrio}
                                        onChange={handleConsumptionChange}
                                    />
                                    <small className="text-muted">Dejar en blanco o cero si no aplica.</small>
                                    {horasUnidadFrioAnterior > consumptionData.horasUnidadFrio && (
                                        <div className='invalid-feedback'>Horas Unidad de Frio actual debe ser mayor al anterior</div>
                                    )}
                                </div>
                                <div className="mb-3 form-group">
                                    <label htmlFor="fechaRegistro" className="form-label">Fecha de Registro</label>
                                    <input
                                        type="date"
                                        id="fechaRegistro"
                                        className="form-control text-end"
                                        name="fechaRegistro"
                                        onFocus={(e) => e.target.select()}
                                        value={consumptionData.fechaRegistro}
                                        onChange={handleConsumptionChange}
                                    />
                                </div>
                                {errorEmployee && <div className='alert alert-danger'>{errorEmployee}</div>}
                                {/* Botón de registrar */}
                                <div className="text-center mt-4 mb-3">
                                    <button
                                        className={`btn w-50 ${isRegisterDisabled || isSubmitting ? 'btn-secondary opacity-50' : 'btn-success'}`}
                                        onClick={() => {
                                            if (consumptionData.cantidadLitros >= (Number(promedioMediaConsumo.MediaLitros) * factorLitros)) {
                                                // Si el valor excede el límite, mostramos el modal de confirmación.
                                                confirmSaveEvenIfHigh();
                                            } else {
                                                // Caso normal, se registra sin advertencia.
                                                registerConsumption();
                                            }
                                        }}
                                        disabled={isRegisterDisabled || isSubmitting}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Guardando...
                                            </>
                                        ) : (
                                            <>
                                                Registrar
                                                <i className={`ms-2 ${isRegisterDisabled ? 'fas fa-lock' : 'fas fa-save'}`}></i>
                                            </>
                                        )}
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>
                )}
                {activeTab === "historial" && (
                    <div className="tab-pane active">
                        <HistorialConsumoCombustible />
                    </div>
                )}
                {/*Boostrap toast */}
                <div className={`toast position-fixed bg-success text-white top-0 end-0 m-3 ${showToast ? 'show' : ''}`}>
                    <div className="toast-header ">
                        <i className="fa fa-check-circle-text text-success m-2"></i>
                        <strong className="me-auto">Éxito</strong>
                        <button type='button' className="btn-close btn-close-white" data-bs-dismiss="toast" aria-label='Close' onClick={() => setShowToast(false)}></button>
                    </div>
                    <div className="toast-body">
                        Datos guardados exitosamente
                    </div>
                </div>
            </div>
        </div >
    );
}
export default ConsumoCombustibleVehiculo;
